import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import {
    detectPushEnv, subscribeToPush, ensureRegistration, saveSubscription,
    reportDiag, getSwVersion, copySiteLink, ERROR_TEXT,
} from "@/lib/pushClient";

/**
 * Header bell — opt-in to push notifications.
 *  - Nothing happens before the reader clicks (no prompt on load).
 *  - "enabled" is shown ONLY after browser subscribe + server save succeed.
 *  - In-app browsers (Facebook/Instagram/TikTok/WebView) never call
 *    subscribe(); they get a clear "open in Chrome" message + copy link.
 *  - Silent self-heal when permission is already granted but the local
 *    subscription is missing (desktop Chrome/Edge rotate FCM endpoints).
 *  - Failures are reported to the admin-only diagnostics log (no secrets).
 */
const DENIED_KEY = "notification-bell-denied-at";
const OPT_OUT_KEY = "push-opt-out"; // set when the reader cancels; blocks silent re-subscribe
const ENDPOINT_KEY = "push-endpoint"; // last endpoint we registered — lets us release it if permission is revoked

export default function NotificationBell({ className = "" }) {
    const [state, setState] = useState("idle"); // idle | enabled | unsupported | ios-hint | in-app
    const [busy, setBusy] = useState(false);
    const { t } = useLang();
    const regRef = useRef(null);
    const healedRef = useRef(false);

    const silentHeal = useCallback(async () => {
        if (healedRef.current) return;
        healedRef.current = true;
        try {
            if (Notification.permission !== "granted") return;
            if (localStorage.getItem(OPT_OUT_KEY)) return; // the reader cancelled — never auto re-enable
            const { sub, reg } = await subscribeToPush(); // no prompt: permission already granted
            regRef.current = reg;
            if (sub) setState("enabled");
        } catch (e) {
            console.warn("silent heal failed:", e?.errName || e?.name || e);
        }
    }, []);

    const detect = useCallback(async () => {
        const env = detectPushEnv();
        if (env.kind === "ios-browser") { setState("ios-hint"); return; }
        if (env.kind === "in-app") { setState("in-app"); return; }
        if (env.kind !== "ok") { setState("unsupported"); return; }
        try {
            const reg = await navigator.serviceWorker.getRegistration("/");
            regRef.current = reg || null;
            const sub = reg ? await reg.pushManager.getSubscription() : null;
            if (Notification.permission === "denied") {
                // Revoked from browser settings → release the server row for THIS device once.
                const ep = (() => { try { return localStorage.getItem(ENDPOINT_KEY); } catch { return null; } })();
                if (ep) { try { await api.post("/public/push/unsubscribe", { endpoint: ep }); localStorage.removeItem(ENDPOINT_KEY); } catch {} }
                setState("idle"); return;
            }
            if (sub && Notification.permission === "granted") {
                try { localStorage.setItem(ENDPOINT_KEY, sub.toJSON().endpoint); } catch {}
                const r = await api.post("/public/push/verify", { endpoint: sub.toJSON().endpoint }).then((x) => x.data).catch(() => null);
                if (r && r.known && r.active === false) {
                    // Cancelled on the server (this device) → mirror it locally, stay idle.
                    try { await sub.unsubscribe(); } catch {}
                    setState("idle");
                    return;
                }
                if (!r || r.known === false) { try { await saveSubscription(sub); } catch {} }
                setState("enabled");
                return;
            }
            if (Notification.permission === "granted" && !sub) { setState("idle"); silentHeal(); return; }
        } catch { /* ignore */ }
        setState("idle");
    }, [silentHeal]);

    useEffect(() => { detect(); }, [detect]);

    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === "visible") { healedRef.current = false; detect(); } };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [detect]);

    const doSubscribe = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (Notification.permission === "default") {
                toast(t("pushHint"), { duration: 2500 });
                await new Promise((r) => setTimeout(r, 700));
            }
            const { reg, sub: newSub } = await subscribeToPush();
            regRef.current = reg;
            try { localStorage.removeItem(OPT_OUT_KEY); if (newSub) localStorage.setItem(ENDPOINT_KEY, newSub.toJSON().endpoint); } catch {}
            setState("enabled");
            toast.success(t("pushEnabled"));
        } catch (e) {
            const name = e?.errName || e?.name;
            if (name === "Dismissed") return; // user closed the prompt — silent
            if (name === "NotAllowedError") { try { localStorage.setItem(DENIED_KEY, String(Date.now())); } catch {} }
            const swVersion = await getSwVersion(regRef.current);
            reportDiag({ stage: e?.stage || "unknown", errName: name, swVersion });
            toast.error(e?.message || ERROR_TEXT.default, { duration: 7000 });
            setState("idle"); // never show "enabled" after a failure
        } finally {
            setBusy(false);
        }
    };

    const doUnsubscribe = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                // 1) server first — the row must be inactive before we drop the browser subscription
                try {
                    await api.post("/public/push/unsubscribe", { endpoint: sub.toJSON().endpoint });
                } catch {
                    toast.error(t("pushUnsubFail"), {
                        duration: 7000, action: { label: t("retry"), onClick: () => doUnsubscribe() },
                    });
                    return; // still subscribed; no false success
                }
                // 2) then the browser
                try { await sub.unsubscribe(); } catch {}
            }
            try { localStorage.setItem(OPT_OUT_KEY, String(Date.now())); localStorage.removeItem(ENDPOINT_KEY); } catch {}
            setState("idle");
            toast(t("pushDisabled"));
        } finally { setBusy(false); }
    };

    const onClick = async () => {
        if (busy) return;
        if (state === "unsupported") { toast(t("bellUnsupportedToast")); return; }
        if (state === "in-app") {
            const copied = await copySiteLink();
            toast(t("bellInAppToast") + (copied ? " " + t("bellLinkCopied") : ""), { duration: 7000 });
            reportDiag({ stage: "env", errName: "InAppBrowser" });
            return;
        }
        if (state === "ios-hint") {
            toast(t("bellIosToast"), { duration: 5500 });
            return;
        }
        if (state === "enabled") return doUnsubscribe();
        return doSubscribe();
    };

    const label = t({ idle: "bellIdle", enabled: "bellEnabled", "ios-hint": "bellIos", "in-app": "bellInApp", unsupported: "bellUnsupported" }[state]);
    const Icon = state === "enabled" ? BellRing : (state === "unsupported" ? BellOff : Bell);
    const color = state === "enabled" ? "text-brand-red" : "text-brand-black hover:text-brand-red";

    return (
        <button type="button" onClick={onClick} disabled={busy} aria-label={label} aria-pressed={state === "enabled"} title={label}
            className={`glow-target inline-flex items-center justify-center h-11 w-11 rounded-md transition-colors ${color} ${busy ? "opacity-60" : ""} ${className}`}
            data-testid="notification-bell" data-state={state}>
            <Icon className="w-4 h-4" aria-hidden="true" />
        </button>
    );
}
